/**
 * Shared support chat helpers — aligned with Flutter ChatMessageController / ChatMessagesScreen:
 * sort by time ascending, sender identity from sender.user_login.ID, sent-cache for optimistic match,
 * grouped bubbles (first vs continuation).
 */

export const CHAT_SENT_CACHE_KEY = "support_chat_recent_sent_messages";

export function normalizeChatText(value) {
  return (value || "")
    .toString()
    .toLowerCase()
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function parseMessageTime(value) {
  if (!value) return null;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
}

export function persistRecentSentMessages(items) {
  try {
    sessionStorage.setItem(CHAT_SENT_CACHE_KEY, JSON.stringify(items));
  } catch (_) {}
}

export function loadRecentSentCacheIntoRef(recentSentMessagesRef) {
  try {
    const raw = sessionStorage.getItem(CHAT_SENT_CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;
    const now = Date.now();
    recentSentMessagesRef.current = parsed
      .filter((item) => item && typeof item.text === "string" && item.sentAt)
      .filter((item) => now - Number(item.sentAt) <= 48 * 60 * 60 * 1000)
      .slice(-100);
  } catch (_) {}
}

export function enrichMessagesWithSentCache(messages, sentCache, now = Date.now()) {
  const cache = sentCache || [];
  return (messages || []).map((msg) => {
    const text = normalizeChatText(msg?.text);
    if (!text) return { ...msg, clientSentMatch: false };
    const msgTime = parseMessageTime(msg?.createdAt);
    const matchedRecent = cache.some((sent) => {
      const sentText = normalizeChatText(sent?.text);
      if (!sentText || sentText !== text) return false;
      const sentAt = Number(sent.sentAt) || now;
      if (!msgTime) return now - sentAt <= 48 * 60 * 60 * 1000;
      return Math.abs(msgTime - sentAt) <= 48 * 60 * 60 * 1000;
    });
    return { ...msg, clientSentMatch: matchedRecent };
  });
}

export function sortChatMessagesAsc(messages) {
  return [...(messages || [])].sort((a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : null;
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : null;
    if (aTime && bTime && aTime !== bTime) return aTime - bTime;
    if (aTime && !bTime) return -1;
    if (!aTime && bTime) return 1;
    const aId = Number(a?.id ?? 0);
    const bId = Number(b?.id ?? 0);
    return aId - bId;
  });
}

function messageDedupeKey(m) {
  if (m?.id != null && String(m.id) !== "") return `id:${String(m.id)}`;
  const t = normalizeChatText(m?.text);
  const tm = m?.createdAt ? String(m.createdAt) : "";
  return `tmp:${t}:${tm}:${m?.clientSentMatch ? "1" : "0"}`;
}

/**
 * Merge lists by stable id when present; otherwise by weak key. Output sorted ascending by time.
 */
export function mergeMessagesById(existing, incoming) {
  const map = new Map();
  const add = (m) => {
    map.set(messageDedupeKey(m), m);
  };
  (existing || []).forEach(add);
  (incoming || []).forEach(add);
  return sortChatMessagesAsc([...map.values()]);
}

/** All stored login / user ids (dashboard and login may use different keys). */
function collectId(value, ids) {
  if (value != null && String(value).trim() !== "") {
    ids.add(String(value).trim());
  }
}

export function getStoredUserLoginIds() {
  if (typeof window === "undefined") return [];
  const ids = new Set();
  for (const key of ["userId", "user_id", "USER_ID"]) {
    collectId(localStorage.getItem(key) || sessionStorage.getItem(key), ids);
  }
  try {
    const raw = sessionStorage.getItem("userData");
    if (raw) {
      const data = JSON.parse(raw);
      collectId(data?.ID ?? data?.id, ids);
      collectId(data?.USER_ID ?? data?.user_id, ids);
      collectId(data?.USER_LOGIN_ID ?? data?.user_login_id, ids);
      const ul = data?.user_login ?? data?.USER_LOGIN;
      if (ul && typeof ul === "object") {
        collectId(ul.ID ?? ul.id, ids);
      }
    }
  } catch (_) {}
  return [...ids];
}

function isTruthyFlag(value) {
  return value === 1 || value === true || value === "1" || value === "true";
}

function isFalsyFlag(value) {
  return value === 0 || value === false || value === "0" || value === "false";
}

/**
 * Whether the message was sent by the logged-in customer (right-aligned bubble).
 * API flags (IS_FROM_USER / FROM_USER / IS_ME_SENDER) take precedence over sender id.
 */
export function resolveIsMeSender(msg, currentUserIdStrOrIds) {
  const storedIds = Array.isArray(currentUserIdStrOrIds)
    ? currentUserIdStrOrIds.map((id) => String(id).trim()).filter(Boolean)
    : currentUserIdStrOrIds
      ? [String(currentUserIdStrOrIds).trim()]
      : getStoredUserLoginIds();

  const matchesStoredId = (id) =>
    id != null && String(id).trim() !== "" && storedIds.includes(String(id).trim());

  if (isTruthyFlag(msg?.isMeSender)) return true;
  if (isFalsyFlag(msg?.isMeSender)) return false;

  if (isTruthyFlag(msg?.isFromUser)) return true;
  if (isFalsyFlag(msg?.isFromUser)) return false;

  if (isTruthyFlag(msg?.isSupportMessage)) return false;
  if (isFalsyFlag(msg?.isSupportMessage)) return true;

  if (msg?.clientSentMatch) return true;

  const senderId =
    msg?.senderUserId != null && String(msg.senderUserId).trim() !== ""
      ? String(msg.senderUserId).trim()
      : null;

  if (senderId && storedIds.length > 0) {
    return matchesStoredId(senderId);
  }

  const fromUserValue = msg?.fromUser;
  if (isTruthyFlag(fromUserValue)) return true;
  if (isFalsyFlag(fromUserValue)) return false;

  const senderTypeValue = (msg?.senderType || "").toString().toLowerCase();
  if (
    senderTypeValue.includes("support") ||
    senderTypeValue.includes("admin") ||
    senderTypeValue.includes("agent") ||
    senderTypeValue.includes("staff")
  ) {
    return false;
  }
  if (
    senderTypeValue.includes("user") ||
    senderTypeValue.includes("client") ||
    senderTypeValue.includes("customer")
  ) {
    return true;
  }

  return false;
}

/**
 * @param {Array} messages
 * @param {string} currentUserIdStr
 * @returns {Array} messages with isMe + firstInSequence (like MessageBubble.first / .next)
 */
export function buildDisplayMessages(messages, currentUserIdStr) {
  const sorted = sortChatMessagesAsc(messages);
  const withMe = sorted.map((msg) => ({
    ...msg,
    isMe: resolveIsMeSender(msg, currentUserIdStr),
  }));
  return withMe.map((msg, i) => {
    const prev = i > 0 ? withMe[i - 1] : null;
    const firstInSequence =
      !prev ||
      msg.isMe !== prev.isMe ||
      String(msg?.senderUserId ?? "") !== String(prev?.senderUserId ?? "");
    return { ...msg, firstInSequence };
  });
}
